import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import React__default from 'react';
import styled, { css } from 'styled-components';
import Box from '../Box/Box.js';
import Text from '../Text/Text.js';
import FuegokitLink from '../Link/Link.js';
import Button from '../Button/Button.js';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.js';
import Logo from '../Logo/Logo.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const HEADER_HEIGHT = 140;

// --------------------------------------------------------
// GlobalHeader Root
const Wrapper = styled(Box).withConfig({
  displayName: "GlobalHeader__Wrapper",
  componentId: "sc-1gymzma-0"
})(["position:", ";top:0;z-index:", ";background-color:", ";width:100%;border-bottom:", ";border-bottom-color:", ";@media (min-width:", "){&:after{display:", ";content:'';position:absolute;left:0px;right:0px;top:100%;height:4px;background:linear-gradient( rgba(9,30,66,0.13) 0px,rgba(9,30,66,0.13) 1px,rgba(9,30,66,0.08) 1px,rgba(9,30,66,0) 4px );}transition:background-color 150ms cubic-bezier(0.32,0,0.67,0) 0ms;}transition:background-color,border-color 150ms cubic-bezier(0.32,0,0.67,0) 0ms;", ""], p => p.isSticky ? 'sticky' : 'relative', p => p.isSticky && p.isBorderVisible ? 2 : 'revert', themeGet('colors.elevation.surface.raised.default'), p => p.isBorderVisible ? '1px solid' : 'revert', p => p.isBorderVisible ? themeGet('colors.border.subtle') : '', themeGet('breakpoints.1'), p => p.isBorderVisible ? 'block' : 'none', sx);
const StyledNavTitle = styled(Text).withConfig({
  displayName: "GlobalHeader__StyledNavTitle",
  componentId: "sc-1gymzma-1"
})(["text-decoration:none;font-size:", ";font-weight:", ";"], themeGet('fontSizes.6'), themeGet('fontWeights.semibold'));
const StyledNavSubtitle = styled(Text).withConfig({
  displayName: "GlobalHeader__StyledNavSubtitle",
  componentId: "sc-1gymzma-2"
})(["text-decoration:none;font-size:", ";color:", ";font-weight:", ";"], themeGet('fontSizes.3'), themeGet('colors.text.subtle'), themeGet('fontWeights.medium'));
const StyledHomeLink = styled(FuegokitLink).withConfig({
  displayName: "GlobalHeader__StyledHomeLink",
  componentId: "sc-1gymzma-3"
})(["text-decoration:none;color:", ";display:inline-flex;align-items:center;gap:", ";&:hover{text-decoration:none;}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";border-radius:", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";border-radius:", ";}"], themeGet('colors.text.default'), themeGet('space.2'), themeGet('colors.border.focused'), themeGet('radii.2'), themeGet('colors.border.focused'), themeGet('radii.2'));
const StyledHomeText = styled(Text).withConfig({
  displayName: "GlobalHeader__StyledHomeText",
  componentId: "sc-1gymzma-4"
})(["text-decoration:none;color:", ";display:inline-flex;align-items:center;gap:", ";"], themeGet('colors.text.default'), themeGet('space.2'));
const StyledNav = styled(Box).withConfig({
  displayName: "GlobalHeader__StyledNav",
  componentId: "sc-1gymzma-5"
})(["display:flex;height:", ";align-items:center;justify-content:space-between;background-color:", ";font-size:", ";padding-right:", ";padding-left:", ";@media (min-width:", "){padding-right:", ";padding-left:", ";}transition:height 150ms cubic-bezier(0.17,0.67,0.83,0.67) 0ms;", ""], p => p.resized ? '56px' : `${HEADER_HEIGHT}px`, themeGet('colors.elevation.surface.default.[default]'), themeGet('fontSizes.5'), themeGet('space.3'), themeGet('space.3'), themeGet('breakpoints.2'), themeGet('space.4'), themeGet('space.4'), sx);
// Button
const StyledNavButton = styled(Button).withConfig({
  displayName: "GlobalHeader__StyledNavButton",
  componentId: "sc-1gymzma-6"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;cursor:pointer;text-align:left;font:inherit;display:inline-block;color:inherit;margin-left:", ";font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:10px 12px;border-radius:", ";&:hover{opacity:1;text-decoration:none;color:", ";background-color:", ";}&:focus{opacity:1;text-decoration:none;background-color:", ";&:hover{background-color:", ";}}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}", " ", ""], themeGet('space.4'), themeGet('fontSizes.3'), themeGet('fontWeights.medium'), themeGet('radii.1'), themeGet('colors.link.default'), p => p.isActive ? themeGet('colors.background.selected.default.hovered') : themeGet('colors.background.neutral.subtle.hovered'), p => p.isActive ? themeGet('colors.background.selected.default.[default]') : '', p => p.isActive ? themeGet('colors.background.selected.default.hovered') : themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.border.focused'), themeGet('colors.border.focused'), p => {
  if (p.isActive) {
    return css(["opacity:1;text-decoration:none;color:", ";background-color:", ";"], themeGet('colors.text.selected'), themeGet('colors.background.selected.default.[default]'));
  }
  return null;
}, sx);
const StyledNavLink = styled(Button).withConfig({
  displayName: "GlobalHeader__StyledNavLink",
  componentId: "sc-1gymzma-7"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;cursor:pointer;text-align:left;font:inherit;display:inline-block;color:inherit;margin-left:", ";font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:10px 12px;border-radius:", ";&:hover{opacity:1;text-decoration:none;color:", ";background-color:", ";}&:focus{opacity:1;text-decoration:none;background-color:", ";&:hover{background-color:", ";}}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}", " ", ""], themeGet('space.4'), themeGet('fontSizes.3'), themeGet('fontWeights.medium'), themeGet('radii.1'), themeGet('colors.link.default'), p => p.isActive ? themeGet('colors.background.selected.default.hovered') : themeGet('colors.background.neutral.subtle.hovered'), p => p.isActive ? themeGet('colors.background.selected.default.[default]') : '', p => p.isActive ? themeGet('colors.background.selected.default.hovered') : themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.border.focused'), themeGet('colors.border.focused'), p => {
  if (p.isActive) {
    return css(["opacity:1;text-decoration:none;color:", ";background-color:", ";"], themeGet('colors.text.selected'), themeGet('colors.background.selected.default.[default]'));
  }
  return null;
}, sx);
const StyledContent = styled(Box).withConfig({
  displayName: "GlobalHeader__StyledContent",
  componentId: "sc-1gymzma-8"
})(["@media (min-width:", "){display:flex;align-items:center;gap:", ";}"], themeGet('breakpoints.1'), themeGet('space.2'));
const StyledDesktopActions = styled(Box).withConfig({
  displayName: "GlobalHeader__StyledDesktopActions",
  componentId: "sc-1gymzma-9"
})(["display:none;@media (min-width:", "){list-style:none;display:flex;margin-left:24px;white-space:nowrap;margin-left:auto;align-items:center;}"], themeGet('breakpoints.0'));
const StyledNavItem = styled(Box).withConfig({
  displayName: "GlobalHeader__StyledNavItem",
  componentId: "sc-1gymzma-10"
})(["", ""], sx);

/**
 *
 * Contains all the parts of a GlobalHeader
 *
 */
const Root = ({
  children,
  isSticky = true,
  ...props
}) => {
  const [isBorderVisible, setIsBorderVisible] = React__default.useState(false);
  const [resized, setResized] = React__default.useState(false);
  const [scrollTop, setScrollTop] = React__default.useState(0);

  // When handleScroll event is triggered, we update the scrollTop state with current scroll position
  const handleScroll = () => {
    setScrollTop(window.scrollY);
  };
  React__default.useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 1. Track scroll position based on scrollTop state
  // 2. Change border visible and resize state
  React__default.useEffect(() => {
    // If it is resized scroll position will be smaller, so threshold should be smaller
    const HEADER_BORDER_HIDE_THRESHOLD = resized ? 20 : 102;

    // @TODO: improve this logic?
    // show/hide the border at a certain threshold
    if (scrollTop > HEADER_BORDER_HIDE_THRESHOLD) {
      setIsBorderVisible(true);
      setResized(true);
    } else if (scrollTop < HEADER_BORDER_HIDE_THRESHOLD) {
      setIsBorderVisible(false);
      setResized(false);
    }
  }, [scrollTop]);
  return /*#__PURE__*/React__default.createElement(Wrapper, _extends({
    isBorderVisible: isBorderVisible,
    isSticky: isSticky
  }, props), React__default.Children.map(React__default.Children.toArray(children), (child, idx) => {
    if (!child) {
      return null;
    }
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      React__default.createElement(React__default.Fragment, {
        key: idx
      }, resized ? /*#__PURE__*/React__default.cloneElement(child, {
        resized
      }) : child)
    );
  }));
};
Root.displayName = "Root"; // --------------------------------------------------------
// GlobalHeader Nav
/**
 * Nav for Global Header
 */
const Nav = ({
  children,
  resized,
  isConstrained,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledNav, _extends({
    as: "nav",
    "aria-labelledby": "mainmenulabel",
    resized: resized,
    isConstrained: isConstrained
  }, props), React__default.Children.map(React__default.Children.toArray(children), (child, idx) => {
    if (!child) {
      return null;
    }
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      React__default.createElement(React__default.Fragment, {
        key: idx
      }, resized ? /*#__PURE__*/React__default.cloneElement(child, {
        resized
      }) : child)
    );
  }));
};
Nav.displayName = "Nav"; // --------------------------------------------------------
// GlobalHeader Item
/**
 * Navigational link item for Global Header
 */
const NavItem = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledNavItem, _extends({
    as: "li"
  }, props), children);
};
NavItem.displayName = "NavItem"; // --------------------------------------------------------
// GlobalHeader Link
/**
 * Navigation link item for Global Header
 */
const NavLink = ({
  children,
  href,
  onSelect,
  ...props
}) => {
  if (href && onSelect) {
    throw new Error(`GlobalHeaderNav Links take an onSelect or an href prop, but not both. See: https://fuegokit.design/react/patterns/global-header.`);
  }
  if (href) {
    return /*#__PURE__*/React__default.createElement(StyledNavLink, _extends({
      href: href
    }, props), children);
  }
  if (onSelect) {
    return /*#__PURE__*/React__default.createElement(StyledNavButton, _extends({
      onClick: onSelect
    }, props), children);
  }
  throw new Error(`Provide GlobalHeader.NavLink an onSelect or an href prop. See: https://fuegokit.design/react/patterns/global-header.`);
};

// --------------------------------------------------------
// GlobalHeader NavTitle
const NavTitleContent = ({
  appLogoUrl,
  appLogoImgAlt,
  subtitle,
  title,
  resized
}) => {
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      color: 'text.default'
    }
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    id: "mainmenulabel"
  }, "Main menu"), appLogoUrl ? /*#__PURE__*/React__default.createElement(Box, {
    as: "img",
    src: appLogoUrl,
    alt: appLogoImgAlt,
    style: subtitle && !resized ? {
      height: '44px',
      width: '44px'
    } : {
      height: '20px',
      width: '20px'
    },
    sx: {
      display: 'block'
    }
  }) : /*#__PURE__*/React__default.createElement(Logo, {
    variant: "solid",
    size: 20,
    color: "inherit",
    "aria-hidden": "true"
  })), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      alignItems: 'flex-start'
    }
  }, /*#__PURE__*/React__default.createElement(StyledNavTitle, null, title), subtitle && !resized && /*#__PURE__*/React__default.createElement(StyledNavSubtitle, null, subtitle)));
};
/**
 * NavTitle. Must be rendered inside GlobalHeader.Nav.
 */
const NavTitle = props => {
  const {
    appLogoImgAlt,
    appLogoUrl,
    href
  } = props;
  if (appLogoUrl && !appLogoImgAlt) {
    console.warn(`You must include accessible alt text to describe your logo image. Please refer to https://www.w3.org/WAI/tutorials/images/.`);
  }
  return href ? /*#__PURE__*/React__default.createElement(StyledHomeLink, {
    href: href
  }, /*#__PURE__*/React__default.createElement(NavTitleContent, props)) : /*#__PURE__*/React__default.createElement(StyledHomeText, null, /*#__PURE__*/React__default.createElement(NavTitleContent, props));
};

// --------------------------------------------------------
// GlobalHeader Content
/**
 * Navigation content. Must be rendered inside GlobalHeader.Nav.
 */
const Content = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledContent, null, children);
};
Content.displayName = "Content"; // --------------------------------------------------------
// GlobalHeader Navigation List
/**
 * Contains the top level menu items.
 */
const NavList = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledDesktopActions, {
    as: "ul"
  }, children);
};
NavList.displayName = "NavList";
// Exports
Root.displayName = 'GlobalHeader';
Nav.displayName = 'GlobalHeader.Nav';
NavTitle.displayName = 'GlobalHeader.NavTitle';
NavItem.displayName = 'GlobalHeader.NavItem';
NavLink.displayName = 'GlobalHeader.NavLink';
Content.displayName = 'GlobalHeader.Content';
NavList.displayName = 'GlobalHeader.NavList';

/**
 * Displays a collection of navigation links and items for navigating
 */
var GlobalHeader = Object.assign(Root, {
  Nav,
  NavTitle,
  NavItem,
  NavLink,
  Content,
  NavList
});

export { GlobalHeader as default };
